<template>
  <div class="szw-network">

    <!-- Hero -->
    <section class="szw-network-hero">
      <div class="szw-network-hero__bg">
        <svg class="szw-network-hero__bg-shape szw-network-hero__bg-shape--right" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,1000 C300,800 400,1000 700,800 C1000,600 1000,0 1000,0 L0,0 Z" fill="#ffffff" opacity="0.05"/>
        </svg>
        <div class="szw-network-hero__blob szw-network-hero__blob--teal"></div>
        <div class="szw-network-hero__blob szw-network-hero__blob--gold"></div>
      </div>

      <div class="szw-container szw-network-hero__content">
        <div class="tw-flex items-center gap-3">
          <div class="szw-network-hero__icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 28px; color: #071c5b;">hub</span>
          </div>
          <div>
            <h1 class="szw-network-hero__title">Mijn Netwerk</h1>
            <p class="szw-network-hero__subtitle">Jouw persoonlijk telefoonboek van zorgprofessionals</p>
          </div>
          <span v-if="contacts.length" class="szw-network-hero__count">
            {{ contacts.length }} {{ contacts.length === 1 ? 'contact' : 'contacten' }}
          </span>
        </div>

        <!-- Search -->
        <div v-if="contacts.length" class="szw-network-hero__search-wrapper">
          <div class="szw-network-hero__search-glow"></div>
          <div class="szw-network-hero__search-box">
            <span class="material-symbols-outlined szw-network-hero__search-icon">search</span>
            <input
              v-model="searchQuery"
              class="szw-network-hero__search-input"
              placeholder="Zoek op naam, functie of nota..."
              type="text"
            />
            <button v-if="searchQuery" class="szw-network-hero__search-clear" @click="searchQuery = ''">
              <span class="material-symbols-outlined" style="font-size: 18px;">close</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Content -->
    <section class="szw-section">
      <div class="szw-container">

        <!-- Empty state -->
        <div v-if="!contacts.length" class="szw-network__empty">
          <div class="szw-network__empty-icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">group</span>
          </div>
          <h2 class="szw-network__empty-title">Jouw netwerk is nog leeg</h2>
          <p class="szw-network__empty-body">Voeg zorgprofessionals toe via het ster-icoontje op hun profiel om ze hier te bewaren.</p>
          <button class="szw-network__btn szw-network__btn--primary" @click="$router.push('/actors-simplified')">
            <span class="material-symbols-outlined" style="font-size: 18px;">person_search</span>
            Ontdek zorgverleners
          </button>
        </div>

        <!-- No search results -->
        <div v-else-if="!filteredContacts.length" class="szw-network__empty">
          <div class="szw-network__empty-icon-wrap">
            <span class="material-symbols-outlined" style="font-size: 48px; color: #071c5b; opacity: 0.35;">search_off</span>
          </div>
          <p class="szw-network__empty-body">Geen contacten gevonden voor "{{ searchQuery }}"</p>
          <button class="szw-network__btn szw-network__btn--ghost" @click="searchQuery = ''">
            Wis zoekopdracht
          </button>
        </div>

        <!-- Contact grid -->
        <div v-else class="szw-network__grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="contact in filteredContacts"
            :key="contact.elastic_id"
            class="szw-nc-card"
          >
            <!-- Card header -->
            <div class="szw-nc-card__top tw-flex items-center gap-3">
              <div class="szw-nc-card__avatar">
                <ActorAvatar :actor="contact" v-if="contact"/>
                <span class="material-symbols-outlined" style="font-size: 28px; color: #071c5b;">person</span>
              </div>
              <div class="szw-nc-card__info" style="flex: 1; min-width: 0;">
                <h3 class="szw-nc-card__name">{{ contact.name || 'Onbekend' }}</h3>
                <p class="szw-nc-card__role">{{ contact.role || 'Zorgprofessional' }}</p>
              </div>
              <div class="tw-flex gap-1 flex-shrink-0">
                <button
                  class="szw-nc-card__action-btn"
                  title="Bekijk profiel"
                  @click="$router.push('/actors/' + contact.elastic_id)"
                >
                  <span class="material-symbols-outlined">open_in_new</span>
                </button>
                <template v-if="confirmDeleteId === contact.elastic_id">
                  <button
                    class="szw-nc-card__action-btn szw-nc-card__action-btn--confirm-delete"
                    title="Bevestig verwijderen"
                    @click="removeContact(contact.elastic_id)"
                  >
                    <span class="material-symbols-outlined">check</span>
                  </button>
                  <button
                    class="szw-nc-card__action-btn"
                    title="Annuleren"
                    @click="confirmDeleteId = null"
                  >
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </template>
                <button
                  v-else
                  class="szw-nc-card__action-btn szw-nc-card__action-btn--danger"
                  title="Verwijder uit netwerk"
                  @click="confirmDeleteId = contact.elastic_id"
                >
                  <span class="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>

            <!-- Phone field -->
            <div class="szw-nc-card__field">
              <span class="material-symbols-outlined szw-nc-card__field-icon">phone</span>
              <template v-if="editingId === contact.elastic_id + '_phone'">
                <input
                  :ref="'phone_' + contact.elastic_id"
                  v-model="editDraft.phone"
                  class="szw-nc-card__field-input"
                  placeholder="+31 ..."
                  type="tel"
                  @keyup.enter="saveEdit(contact.elastic_id)"
                  @keyup.esc="cancelEdit"
                />
                <div class="szw-nc-card__edit-actions">
                  <button class="szw-nc-card__confirm-btn" title="Opslaan" @mousedown.prevent="saveEdit(contact.elastic_id)">
                    <span class="material-symbols-outlined">check</span>
                  </button>
                  <button class="szw-nc-card__cancel-btn" title="Annuleren" @mousedown.prevent="cancelEdit">
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              </template>
              <span
                v-else
                class="szw-nc-card__field-value"
                :class="{ 'szw-nc-card__field-value--empty': !contact.phone }"
                @click="startEdit(contact, 'phone')"
              >
                {{ contact.phone || 'Voeg gsm-nummer toe...' }}
              </span>
            </div>

            <!-- Notes field -->
            <div class="szw-nc-card__field szw-nc-card__field--notes">
              <span class="material-symbols-outlined szw-nc-card__field-icon">notes</span>
              <template v-if="editingId === contact.elastic_id + '_notes'">
                <textarea
                  :ref="'notes_' + contact.elastic_id"
                  v-model="editDraft.notes"
                  class="szw-nc-card__field-input szw-nc-card__field-input--textarea"
                  placeholder="Persoonlijke nota's..."
                  rows="3"
                  @keyup.esc="cancelEdit"
                />
                <div class="szw-nc-card__edit-actions szw-nc-card__edit-actions--col">
                  <button class="szw-nc-card__confirm-btn" title="Opslaan" @mousedown.prevent="saveEdit(contact.elastic_id)">
                    <span class="material-symbols-outlined">check</span>
                  </button>
                  <button class="szw-nc-card__cancel-btn" title="Annuleren" @mousedown.prevent="cancelEdit">
                    <span class="material-symbols-outlined">close</span>
                  </button>
                </div>
              </template>
              <span
                v-else
                class="szw-nc-card__field-value szw-nc-card__field-value--notes"
                :class="{ 'szw-nc-card__field-value--empty': !contact.notes }"
                @click="startEdit(contact, 'notes')"
              >
                {{ contact.notes || 'Voeg persoonlijke nota toe...' }}
              </span>
            </div>

          </div>
        </div>

      </div>
    </section>

    <SimplifiedFooterSamergo/>

    <!-- FAB: discover providers -->
    <button class="szw-fab" @click="$router.push('/actors-simplified')">
      <span class="material-symbols-outlined" style="font-size: 24px;">person_search</span>
    </button>

  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import SimplifiedFooterSamergo from '@/components/SimplifiedFooter/SimplifiedFooterSamergo.vue'
  import { ACTION_TYPES } from '@/store/modules/network-contacts.js'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'

  export default defineComponent({
    name: 'StaticHomeSamergoNetwork',
    components: { ActorAvatar, SimplifiedFooterSamergo },
    data() {
      return {
        searchQuery: '',
        editingId: null as string | null,
        editDraft: { phone: '', notes: '' },
        confirmDeleteId: null as string | null,
      }
    },
    mounted() {
      this.$store.dispatch(ACTION_TYPES.FETCH)
    },
    computed: {
      contacts() {
        return this.$store.getters.networkContacts
      },
      filteredContacts() {
        if (!this.searchQuery.trim()) return this.contacts
        const q = this.searchQuery.toLowerCase()
        return this.contacts.filter((c: any) =>
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.role && c.role.toLowerCase().includes(q)) ||
          (c.notes && c.notes.toLowerCase().includes(q))
        )
      },
    },
    methods: {
      removeContact(elasticId: string) {
        this.$store.dispatch(ACTION_TYPES.REMOVE_CONTACT, elasticId)
        this.confirmDeleteId = null
      },
      startEdit(contact: any, field: 'phone' | 'notes') {
        this.editingId = contact.elastic_id + '_' + field
        this.editDraft = { phone: contact.phone || '', notes: contact.notes || '' }
        this.$nextTick(() => {
          const refKey = field + '_' + contact.elastic_id
          const el = (this.$refs as any)[refKey]
          if (el) (Array.isArray(el) ? el[0] : el).focus()
        })
      },
      saveEdit(elasticId: string) {
        if (!this.editingId) return
        this.$store.dispatch(ACTION_TYPES.UPDATE_CONTACT, {
          elasticId,
          phone: this.editDraft.phone,
          notes: this.editDraft.notes,
        })
        this.editingId = null
      },
      cancelEdit() {
        this.editingId = null
      },
    },
  })
</script>

<style scoped lang="scss">
  $szw-navy: #071c5b;
  $szw-navy-dark: #00082f;
  $szw-green: #006e29;
  $szw-surface: #f8f9fa;
  $szw-surface-low: #f3f4f5;
  $szw-border: #c6c5d2;
  $szw-text: #191c1d;
  $szw-text-muted: #454650;

  .szw-network {
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    background: $szw-surface;
    min-height: 100vh;
    color: $szw-text;
  }

  .szw-container {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  // ── Hero ──────────────────────────────────────────────────────────────────

  .szw-network-hero {
    position: relative;
    background: $szw-navy;
    padding: 64px 0 80px;
    overflow: hidden;
  }

  .szw-network-hero__bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
  }

  .szw-network-hero__bg-shape {
    position: absolute;

    &--right {
      top: 0;
      right: 0;
      width: 60%;
      height: 100%;
    }
  }

  .szw-network-hero__blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);

    &--teal {
      top: -40px;
      right: 10%;
      width: 300px;
      height: 300px;
      background: #006e29;
      opacity: 0.25;
    }

    &--gold {
      bottom: -60px;
      left: 5%;
      width: 240px;
      height: 240px;
      background: #f5c343;
      opacity: 0.15;
    }
  }

  .szw-network-hero__content {
    position: relative;
    z-index: 10;
  }

  .szw-network-hero__icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: #7df98f;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .szw-network-hero__title {
    font-size: clamp(1.5rem, 3vw, 2.25rem);
    font-weight: 700;
    color: white;
    line-height: 1.2;
    margin: 0;
    letter-spacing: -0.02em;
  }

  .szw-network-hero__subtitle {
    font-size: 0.95rem;
    color: rgba(219, 234, 254, 0.7);
    margin-top: 4px;
  }

  .szw-network-hero__count {
    margin-left: auto;
    background: rgba(255, 255, 255, 0.12);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    padding: 6px 18px;
    border-radius: 9999px;
    white-space: nowrap;
  }

  .szw-network-hero__search-wrapper {
    position: relative;
    margin-top: 32px;
    max-width: 640px;
  }

  .szw-network-hero__search-glow {
    position: absolute;
    inset: -4px;
    background: linear-gradient(to right, #7df98f, #dde1ff);
    border-radius: 16px;
    filter: blur(8px);
    opacity: 0.25;
  }

  .szw-network-hero__search-box {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 14px;
    padding: 6px 6px 6px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }

  .szw-network-hero__search-icon {
    padding: 0 12px 0 16px;
    color: $szw-navy;
    flex-shrink: 0;
  }

  .szw-network-hero__search-input {
    flex: 1;
    padding: 10px 0;
    background: transparent;
    border: none;
    outline: none;
    font-size: 0.95rem;
    font-family: inherit;
    color: $szw-text;

    &::placeholder {
      color: #757681;
    }
  }

  .szw-network-hero__search-clear {
    background: $szw-surface-low;
    border: none;
    color: $szw-text-muted;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 6px;
    border-radius: 8px;
    margin-right: 4px;
    transition: background 0.15s;

    &:hover {
      background: $szw-border;
    }
  }

  // ── Section ───────────────────────────────────────────────────────────────

  .szw-section {
    padding: 64px 0;
  }

  // ── Empty state ───────────────────────────────────────────────────────────

  .szw-network__empty {
    text-align: center;
    padding: 80px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .szw-network__empty-icon-wrap {
    width: 96px;
    height: 96px;
    background: #dde1ff;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .szw-network__empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
  }

  .szw-network__empty-body {
    font-size: 0.95rem;
    color: $szw-text-muted;
    max-width: 400px;
    line-height: 1.6;
    margin: 0;
  }

  .szw-network__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    border: none;
    margin-top: 8px;
    transition: opacity 0.15s, transform 0.1s;

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
    }

    &--primary {
      background: $szw-navy;
      color: white;
    }

    &--ghost {
      background: $szw-surface-low;
      color: $szw-text-muted;
      border: 1px solid $szw-border;
    }
  }

  // ── Contact grid ──────────────────────────────────────────────────────────

  .szw-network__grid {
    // spacing via grid gap
  }

  // ── Contact card ──────────────────────────────────────────────────────────

  .szw-nc-card {
    background: white;
    border: 1px solid $szw-border;
    border-radius: 16px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.2s, transform 0.15s;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.09);
      transform: translateY(-1px);
    }
  }

  .szw-nc-card__top {
    // layout via tw-flex
  }

  .szw-nc-card__avatar {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: #dde1ff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .szw-nc-card__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .szw-nc-card__name {
    font-size: 0.95rem;
    font-weight: 700;
    color: $szw-text;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .szw-nc-card__role {
    font-size: 0.78rem;
    color: $szw-text-muted;
    margin-top: 2px;
  }

  .szw-nc-card__action-btn {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $szw-text-muted;
    transition: background 0.15s, color 0.15s;

    .material-symbols-outlined {
      font-size: 18px;
    }

    &:hover {
      background: $szw-surface-low;
      color: $szw-text;
    }

    &--danger:hover {
      background: #ffdad6;
      color: #ba1a1a;
    }

    &--confirm-delete {
      background: #ba1a1a;
      color: white;

      &:hover {
        background: #93000a;
      }
    }
  }

  // ── Editable fields ───────────────────────────────────────────────────────

  .szw-nc-card__field {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-top: 1px solid $szw-border;
    padding-top: 12px;
  }

  .szw-nc-card__field-icon {
    font-size: 16px;
    color: $szw-text-muted;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .szw-nc-card__field-value {
    font-size: 0.875rem;
    color: $szw-text;
    line-height: 1.5;
    cursor: text;
    flex: 1;
    border-radius: 6px;
    padding: 2px 6px;
    margin: -2px -6px;
    transition: background 0.15s;

    &:hover {
      background: $szw-surface-low;
    }

    &--empty {
      color: #94a3b8;
      font-style: italic;
    }

    &--notes {
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  .szw-nc-card__field-input {
    flex: 1;
    font-size: 0.875rem;
    font-family: inherit;
    color: $szw-text;
    background: $szw-surface-low;
    border: 1.5px solid $szw-navy;
    border-radius: 8px;
    padding: 6px 10px;
    outline: none;
    line-height: 1.5;
    transition: border-color 0.15s;

    &:focus {
      border-color: #4b60b8;
    }

    &--textarea {
      resize: none;
      width: 100%;
    }
  }

  .szw-nc-card__edit-actions {
    display: flex;
    flex-direction: row;
    gap: 4px;
    flex-shrink: 0;
    align-self: flex-start;

    &--col {
      flex-direction: column;
    }
  }

  .szw-nc-card__confirm-btn,
  .szw-nc-card__cancel-btn {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;

    .material-symbols-outlined {
      font-size: 16px;
    }
  }

  .szw-nc-card__confirm-btn {
    background: $szw-navy;
    color: white;

    &:hover {
      background: #4b60b8;
    }
  }

  .szw-nc-card__cancel-btn {
    background: $szw-surface-low;
    color: $szw-text-muted;
    border: 1px solid $szw-border;

    &:hover {
      background: #ffdad6;
      color: #ba1a1a;
      border-color: #ffdad6;
    }
  }

  // ── FAB ───────────────────────────────────────────────────────────────────

  .szw-fab {
    position: fixed;
    bottom: 32px;
    right: 32px;
    width: 56px;
    height: 56px;
    background: $szw-navy;
    color: white;
    border: none;
    border-radius: 50%;
    box-shadow: 0 8px 24px rgba(7, 28, 91, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    z-index: 100;

    &:hover {
      transform: scale(1.08);
      box-shadow: 0 12px 32px rgba(7, 28, 91, 0.45);
    }

    &:active {
      transform: scale(0.95);
    }
  }
</style>
